/**
 * Email Signup Block Component
 *
 * Handles AJAX form submission for email signup, Klaviyo marketing list subscription,
 * and dynamic padding adjustment for integrated buttons.
 */
class EmailSignupBlock extends HTMLElement {
  /** @type {'ready' | 'submitting' | 'submitted' | 'error'} */
  state = 'ready';

  /** @type {AbortController | null} */
  #abortController = null;

  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    // Form elements
    this.form = this.querySelector('form');
    this.emailInput = /** @type {HTMLInputElement | null} */ (this.querySelector('.email-signup__input'));
    this.submitButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('.email-signup__button'));
    this.inputGroup = this.querySelector('.email-signup__input-group');

    // Message elements
    this.ajaxSuccessMessage = /** @type {HTMLElement | null} */ (this.querySelector('[data-ajax-success]'));
    this.ajaxErrorMessage = /** @type {HTMLElement | null} */ (this.querySelector('[data-ajax-error]'));
    this.errorText = this.querySelector('[data-error-text]');

    // Button padding adjustment (existing functionality)
    this.button = /** @type {HTMLElement | null} */ (this.querySelector('.email-signup__button--integrated'));
    const isTextButton = this.button && this.button.classList.contains('email-signup__button--text');

    if (this.button && this.emailInput && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ('fonts' in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }

    // Form submission handler
    this.form?.addEventListener('submit', this.handleSubmit);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.form?.removeEventListener('submit', this.handleSubmit);
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /**
   * Gets the Klaviyo API key from data attribute.
   * @returns {string}
   */
  get apiKey() {
    return this.dataset.klaviyoApiKey || '';
  }

  /**
   * Gets the Klaviyo API version from data attribute.
   * @returns {string}
   */
  get apiVersion() {
    return this.dataset.klaviyoApiVersion || '2024-06-15';
  }

  /**
   * Gets the BCP 47 locale tag from data attribute (e.g. "en-US").
   * @returns {string}
   */
  get locale() {
    return this.dataset.locale || '';
  }

  /**
   * Gets the appropriate Klaviyo list ID based on the customer's country.
   * @returns {string}
   */
  get listId() {
    const country = this.dataset.countryCode || 'US';

    // Canada gets its own list if configured
    if (country === 'CA' && this.dataset.klaviyoListIdCanada) {
      return this.dataset.klaviyoListIdCanada;
    }

    // Non-US countries get international list if configured
    if (country !== 'US' && this.dataset.klaviyoListIdInternational) {
      return this.dataset.klaviyoListIdInternational;
    }

    // Default to US list
    return this.dataset.klaviyoListId || '';
  }

  /**
   * Handles form submission via AJAX.
   * @param {SubmitEvent} event - The submit event.
   */
  async handleSubmit(event) {
    event.preventDefault();

    if (this.state === 'submitting') {
      return;
    }

    const email = this.emailInput?.value?.trim();

    if (!email) {
      return;
    }

    this.state = 'submitting';
    this.#setSubmitButtonDisabled(true);
    this.#hideMessages();

    // Create new AbortController for this submission
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      // Submit to Shopify (creates customer profile)
      await this.#submitToShopify();

      // Show success immediately after Shopify submission succeeds
      this.state = 'submitted';
      this.#showSuccess();

      if (window.htevents) {
        window.htevents.track(
          'Email Subscribed',
          {
            position: this.dataset.hightouchSource || 'Email Signup',
            email,
          },
          { sent_from: 'Hightouch JS' },
        )
        window.htevents.identify({ email }, { sent_from: 'Hightouch JS' })
      }

      // Fire-and-forget Klaviyo subscription - don't block the success UI
      if (this.apiKey && this.listId) {
        this.#subscribeToKlaviyo(email);
      }

      // @ts-ignore - _learnq is a global from Klaviyo's tracking script
      if (this.locale && typeof window._learnq !== 'undefined') {
        // @ts-ignore
        window._learnq.push(['identify', { '$email': email, '$locale': this.locale }]);
      }

      // Clear the input after successful submission
      if (this.emailInput) {
        this.emailInput.value = '';
      }
    } catch (error) {
      // Ignore abort errors (component disconnected)
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Email signup failed:', error);
      this.state = 'error';
      this.#showError();
      this.#setSubmitButtonDisabled(false);
    }
  }

  /**
   * Submits the form to Shopify via AJAX.
   * @returns {Promise<Response>}
   */
  async #submitToShopify() {
    if (!this.form) {
      throw new Error('Form not found');
    }

    const formData = new FormData(this.form);
    const response = await fetch(this.form.action, {
      method: 'POST',
      body: formData,
      signal: this.#abortController?.signal,
    });

    // Shopify returns a redirect on success, which fetch follows
    // We consider it successful if we get any response without throwing
    return response;
  }

  /**
   * Subscribes the email to a Klaviyo marketing list.
   * @param {string} email - The customer's email address.
   * @returns {Promise<Response | void>}
   */
  async #subscribeToKlaviyo(email) {
    const endpoint = `https://a.klaviyo.com/client/subscriptions/?company_id=${this.apiKey}`;

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                ...(this.locale && { locale: this.locale }),
              },
            },
          },
          custom_source: 'Footer Email Signup',
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.listId,
            },
          },
        },
      },
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Revision: this.apiVersion,
        },
        body: JSON.stringify(payload),
        // No abort signal - fire and forget
      });

      if (!response.ok) {
        console.warn(`Klaviyo subscription failed: ${response.status}`);
      }

      return response;
    } catch (error) {
      // Silently ignore Klaviyo subscription errors - don't affect user experience
      console.warn('Klaviyo subscription failed:', error);
    }
  }

  /**
   * Sets the submit button disabled state.
   * @param {boolean} disabled - Whether the button should be disabled.
   */
  #setSubmitButtonDisabled(disabled) {
    if (this.submitButton) {
      this.submitButton.disabled = disabled;
    }
  }

  /**
   * Hides all message containers.
   */
  #hideMessages() {
    this.ajaxSuccessMessage?.classList.add('hidden');
    this.ajaxErrorMessage?.classList.add('hidden');
  }

  /**
   * Shows the success message and hides the form input group.
   */
  #showSuccess() {
    this.ajaxSuccessMessage?.classList.remove('hidden');
    this.ajaxSuccessMessage?.focus();
  }

  /**
   * Shows the error message.
   */
  #showError() {
    this.ajaxErrorMessage?.classList.remove('hidden');
    this.ajaxErrorMessage?.focus();
  }

  /**
   * Adjusts input padding based on integrated button width.
   * (Existing functionality preserved)
   */
  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty('--button-actual-width', `${buttonWidth}px`);
    }
  }
}

if (!customElements.get('email-signup-block')) {
  customElements.define('email-signup-block', EmailSignupBlock);
}
